import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Trash2, Quote, Eye, EyeOff } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { authFetch } from "../lib/auth";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminTestimonials = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    quote: "",
    author_name: "",
    author_role: "",
    sort_order: 0,
  });

  const fetchAll = async () => {
    try {
      const res = await authFetch(`${API}/admin/testimonials`);
      if (res.ok) {
        const data = await res.json();
        setItems(data.testimonials || []);
      }
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.quote.trim() || !form.author_name.trim()) {
      toast.error("Quote and author name are required");
      return;
    }
    setSubmitting(true);
    try {
      const res = await authFetch(`${API}/admin/testimonials`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, is_published: true }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed");
      toast.success("Testimonial added");
      setForm({ quote: "", author_name: "", author_role: "", sort_order: 0 });
      setShowForm(false);
      fetchAll();
    } catch (err) {
      toast.error(err.message || "Failed to add testimonial");
    } finally {
      setSubmitting(false);
    }
  };

  const togglePublished = async (item) => {
    try {
      const res = await authFetch(`${API}/admin/testimonials/${item.testimonial_id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ is_published: !item.is_published }),
      });
      if (!res.ok) throw new Error("Failed");
      fetchAll();
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete testimonial from ${item.author_name}?`)) return;
    try {
      const res = await authFetch(`${API}/admin/testimonials/${item.testimonial_id}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Failed");
      toast.success("Deleted");
      fetchAll();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  return (
    <Card className="bg-card border-border" data-testid="admin-testimonials-section">
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2">
          <Quote className="w-5 h-5 text-primary" />
          Testimonials
          <span className="text-sm text-muted-foreground font-normal">
            ({items.length})
          </span>
        </CardTitle>
        <Button
          size="sm"
          onClick={() => setShowForm(!showForm)}
          data-testid="admin-testimonials-add-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          {showForm ? "Cancel" : "Add testimonial"}
        </Button>
      </CardHeader>
      <CardContent className="space-y-6">
        {showForm && (
          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            className="space-y-4 p-4 bg-background border border-border rounded-md"
            data-testid="admin-testimonials-form"
          >
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Quote</Label>
              <Textarea
                value={form.quote}
                onChange={(e) => setForm({ ...form, quote: e.target.value })}
                placeholder="Six weeks in, I memorised every name at our 40-person team meeting..."
                rows={4}
                data-testid="admin-testimonial-quote"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-sm text-muted-foreground mb-1 block">
                  Author name
                </Label>
                <Input
                  value={form.author_name}
                  onChange={(e) => setForm({ ...form, author_name: e.target.value })}
                  placeholder="Sarah K."
                  data-testid="admin-testimonial-name"
                />
              </div>
              <div>
                <Label className="text-sm text-muted-foreground mb-1 block">
                  Role / context (optional)
                </Label>
                <Input
                  value={form.author_role}
                  onChange={(e) => setForm({ ...form, author_role: e.target.value })}
                  placeholder="Project manager · 6 weeks in"
                  data-testid="admin-testimonial-role"
                />
              </div>
            </div>
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">
                Sort order (lower shows first)
              </Label>
              <Input
                type="number"
                value={form.sort_order}
                onChange={(e) =>
                  setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })
                }
                placeholder="0"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full"
              data-testid="admin-testimonial-submit"
            >
              {submitting ? "Adding…" : "Add testimonial"}
            </Button>
          </motion.form>
        )}

        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            No testimonials yet. Add one and it will appear on the public /science page.
          </p>
        ) : (
          <div className="space-y-2" data-testid="admin-testimonials-list">
            {items.map((t) => (
              <div
                key={t.testimonial_id}
                className="flex items-start gap-3 p-3 bg-background border border-border rounded-md"
              >
                <Quote className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground line-clamp-2">"{t.quote}"</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    — <span className="text-foreground/80">{t.author_name}</span>
                    {t.author_role ? ` · ${t.author_role}` : ""}
                  </p>
                  {!t.is_published && (
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-muted text-muted-foreground border border-border mt-2 inline-block">
                      Draft
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => togglePublished(t)}
                    title={t.is_published ? "Unpublish" : "Publish"}
                  >
                    {t.is_published ? (
                      <Eye className="w-4 h-4 text-primary" />
                    ) : (
                      <EyeOff className="w-4 h-4 text-muted-foreground" />
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => remove(t)}
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminTestimonials;
